import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  setLogs,
  setFilters,
  updateFilter,
  clearFilters,
  setStats,
} from '../store/logSlice';
import * as api from '../api';
import { LogOperation } from '../types';

export function useLogs() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { logs, filters, stats, loading } = useSelector(
    (state: RootState) => state.log
  );

  const logsQuery = useQuery({
    queryKey: ['logs', filters],
    queryFn: () =>
      api.getLogs({
        workspace: filters.workspace,
        agent: filters.agent,
        operation: filters.operation,
        startDate: filters.dateRange?.[0],
        endDate: filters.dateRange?.[1],
      }),
  });

  const statsQuery = useQuery({
    queryKey: ['logStats'],
    queryFn: api.getLogStats,
  });

  const exportLogs = async (format: 'json' | 'csv' = 'json') => {
    const blob = await api.exportLogs(
      {
        workspace: filters.workspace,
        agent: filters.agent,
        operation: filters.operation,
        startDate: filters.dateRange?.[0],
        endDate: filters.dateRange?.[1],
      },
      format
    );

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-logs.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return {
    logs: logsQuery.data || logs,
    filters,
    stats: statsQuery.data || stats,
    loading: logsQuery.isLoading || loading,
    error: logsQuery.error?.message,
    setWorkspaceFilter: (workspace?: string) =>
      dispatch(updateFilter({ workspace })),
    setAgentFilter: (agent?: string) => dispatch(updateFilter({ agent })),
    setOperationFilter: (operation?: LogOperation) =>
      dispatch(updateFilter({ operation })),
    setDateRange: (dateRange?: [string, string]) =>
      dispatch(updateFilter({ dateRange })),
    clearFilters: () => dispatch(clearFilters()),
    exportLogs,
    refresh: () => queryClient.invalidateQueries({ queryKey: ['logs'] }),
  };
}
